import { ReactNode } from 'react';
import { SingleEntityTemplate } from '../../../../api/models/assets';
import { TpDataContext } from '../../../../data/interfaces';
import { TpSectionsIndexContext } from '../../../../components/sections-index/SectionsIndex';
import { TpSingleEntityViewContext } from '../interfaces';
import { getUserError } from '../../../../components/miscUtils';
import EntityImage from './Image';

interface TpExternalDataProps {
  itemId: string;
  dataContext: TpDataContext;
  entityTypeTemplate?: SingleEntityTemplate;
  elementTemplate: any;
  children?: ReactNode;
  sectionIndexContext: TpSectionsIndexContext;
  singleEntityViewContext: TpSingleEntityViewContext;
}

interface TpElementTemplate {
  title: string;
  sectionId: string;
  description?: string;
  resourceType: string; // only support img at the moment
  isGallery?: boolean;
  url: string;
  requiredProperty?: string;
  height?: number;
}

/**
 * A component to render external data for the entity, such as images from URLs.
 * @param props The properties required to render the external data, including item ID, data context, entity template, element template, children, section index context, and single entity view context.
 * @returns A React element that renders the external data, currently supporting images via the EntityImage component.
 */
const ExternalData = (props: TpExternalDataProps) => {
  const elementTemplate = props.elementTemplate as TpElementTemplate;

  if (elementTemplate.resourceType !== 'img') {
    throw getUserError(`Unsupported resourceType for ExternalData element: ${elementTemplate.resourceType}`);
  }

  // At the moment we refer to the EntityImage component for image rendering,
  // as it already implements the required functionality.
  // We just pass the URL from the ExternalData element template.
  // The fileType is set to 'png' to indicate it's an external image.
  // This is ok as EntityImage does not really use the fileType for external URLs.
  // TODO: In the future, if we support more resource types, we might want to refactor this.
  return (
    <EntityImage
      {...props}
      elementTemplate={{
        title: elementTemplate.title,
        sectionId: elementTemplate.sectionId,
        description: elementTemplate.description,
        fileType: 'external', // Use a generic placeholder for external images
        url: elementTemplate.url,
        requiredProperty: elementTemplate.requiredProperty,
        height: elementTemplate.height,
      }}
    />
  );
};

export default ExternalData;
